import { gql } from '@apollo/client';

export const GET_PROPERTY_PERFORMANCE = gql`
  query GetPropertyPerformance($input: PropertyPerformanceListInput!) {
    propertyPerformance(input: $input) {
      items {
        propertyId
        propertyName
        address
        area
        propertyManagerId
        propertyManagerIds
        beds
        baths
        healthScore
        maintenanceScore
        reviewScoreAdjustment
        openCount
        inProgressCount
        urgentCount
        staleCandidateCount
        oldestOpenDays
        completedCount
        reviewCount
        averageReviewPoints
        classifiedReviewCount
        unclassifiedReviewCount
        propertyRelatedReviewCount
        excludedReviewCount
        positivePropertyReviewCount
        negativePropertyReviewCount
        scoreExplanation
        calculatedAt
      }
      total
      page
      limit
    }
  }
`;

export const GET_PROPERTY_PERFORMANCE_SUMMARY = gql`
  query GetPropertyPerformanceSummary($propertyId: ID!) {
    propertyPerformanceSummary(propertyId: $propertyId) {
      propertyId
      propertyName
      address
      area
      healthScore
      maintenanceScore
      reviewScoreAdjustment
      openCount
      inProgressCount
      urgentCount
      staleCandidateCount
      oldestOpenDays
      completedCount
      reviewCount
      averageReviewPoints
      classifiedReviewCount
      unclassifiedReviewCount
      propertyRelatedReviewCount
      excludedReviewCount
      positivePropertyReviewCount
      negativePropertyReviewCount
      scoreExplanation
      calculatedAt
    }
  }
`;

const WORK_ORDER_FIELDS = gql`
  fragment PerformanceWorkOrderFields on WorkOrder {
    id
    description
    status
    workOrderType
    isUrgent
    assignedRoles
    assignedTo {
      id
      firstName
      lastName
    }
    dueDate
    createdAt
    updatedAt
    completedAt
  }
`;

export const GET_PROPERTY_PERFORMANCE_DETAIL = gql`
  ${WORK_ORDER_FIELDS}
  query GetPropertyPerformanceDetail($propertyId: ID!, $input: PropertyPerformanceDetailInput) {
    propertyPerformanceDetail(propertyId: $propertyId, input: $input) {
      property {
        id
        name
        address
        city
        state
        area
        propertyCode
      }
      healthScore
      maintenanceScore
      reviewScoreAdjustment
      classifiedReviewCount
      unclassifiedReviewCount
      propertyRelatedReviewCount
      excludedReviewCount
      positivePropertyReviewCount
      negativePropertyReviewCount
      scoreExplanation
      completedDays
      completedWorkOrderCount
      staleCandidateIds
      openWorkOrders {
        ...PerformanceWorkOrderFields
      }
      recentReviews {
        id
        streamlineFeedbackId
        statusId
        ratingId
        points
        title
        comments
        reviewerName
        sourceName
        reviewCreatedAt
        aiCategory
        aiSentiment
        aiSeverity
        aiPropertyRelated
        aiRelevance
        aiScoreImpact
        aiSummary
        aiReasoning
        aiModel
        aiPromptVersion
        aiClassifiedAt
        aiClassificationError
      }
    }
  }
`;

export const GET_PROPERTY_COMPLETED_WORK_ORDERS = gql`
  ${WORK_ORDER_FIELDS}
  query GetPropertyCompletedWorkOrders($propertyId: ID!, $input: PropertyCompletedWorkOrdersInput!) {
    propertyCompletedWorkOrders(propertyId: $propertyId, input: $input) {
      items {
        ...PerformanceWorkOrderFields
      }
      total
      page
      limit
      hasMore
      completedDays
    }
  }
`;

export const CLASSIFY_PROPERTY_REVIEWS = gql`
  mutation ClassifyPropertyReviews($propertyId: ID!, $limit: Int!, $force: Boolean!) {
    classifyPropertyReviews(propertyId: $propertyId, limit: $limit, force: $force) {
      selectedCount
      classifiedCount
      skippedCount
      failedCount
      remainingCount
      inputTokens
      outputTokens
      model
      promptVersion
    }
  }
`;

const WORK_ORDER_ANALYSIS_FIELDS = gql`
  fragment PropertyWorkOrderAnalysisFields on PropertyWorkOrderAnalysisResult {
    id
    propertyId
    executiveSummary
    issueGroups {
      title
      category
      priority
      activeWorkOrderIds
      relatedCompletedWorkOrderIds
      summary
      recommendedAction
    }
    recurringIssues {
      title
      workOrderIds
      occurrenceCount
      summary
    }
    closureCandidates {
      workOrderId
      confidence
      reason
      evidenceCompletedWorkOrderIds
      recommendation
    }
    priorityActions {
      title
      priority
      workOrderIds
      reason
    }
    workOrderReferences {
      id
      description
      status
      createdAt
      completedAt
    }
    activeWorkOrderCount
    completedContextCount
    inputTokens
    outputTokens
    model
    promptVersion
    generatedAt
    isCurrent
    reused
  }
`;

export const GET_PROPERTY_WORK_ORDER_ANALYSIS = gql`
  ${WORK_ORDER_ANALYSIS_FIELDS}
  query GetPropertyWorkOrderAnalysis($propertyId: ID!) {
    propertyWorkOrderAnalysis(propertyId: $propertyId) {
      ...PropertyWorkOrderAnalysisFields
    }
  }
`;

export const ANALYZE_PROPERTY_WORK_ORDERS = gql`
  ${WORK_ORDER_ANALYSIS_FIELDS}
  mutation AnalyzePropertyWorkOrders($propertyId: ID!, $force: Boolean!) {
    analyzePropertyWorkOrders(propertyId: $propertyId, force: $force) {
      ...PropertyWorkOrderAnalysisFields
    }
  }
`;

const WORK_ORDER_ANALYSIS_DECISION_FIELDS = gql`
  fragment WorkOrderAnalysisDecisionFields on WorkOrderAnalysisDecisionResult {
    id
    analysisId
    propertyId
    workOrderId
    action
    note
    duplicateOfWorkOrderId
    decidedById
    previousStatus
    resultingStatus
    applicationStatus
    errorMessage
    createdAt
  }
`;

export const GET_WORK_ORDER_ANALYSIS_DECISIONS = gql`
  ${WORK_ORDER_ANALYSIS_DECISION_FIELDS}
  query GetWorkOrderAnalysisDecisions($analysisId: ID!) {
    workOrderAnalysisDecisions(analysisId: $analysisId) {
      ...WorkOrderAnalysisDecisionFields
    }
  }
`;

export const DECIDE_WORK_ORDER_ANALYSIS_RECOMMENDATION = gql`
  ${WORK_ORDER_ANALYSIS_DECISION_FIELDS}
  mutation DecideWorkOrderAnalysisRecommendation($input: DecideWorkOrderAnalysisRecommendationInput!) {
    decideWorkOrderAnalysisRecommendation(input: $input) {
      ...WorkOrderAnalysisDecisionFields
    }
  }
`;

const PROPERTY_MANAGER_SUMMARY_FIELDS = gql`
  fragment PropertyManagerSummaryFields on PropertyManagerSummaryResult {
    id
    propertyId
    executiveOverview
    immediatePriorities { title detail workOrderIds }
    completedWorkHighlights { title detail workOrderIds }
    watchItems { title detail workOrderIds }
    workOrderReferences {
      id
      description
      status
      createdAt
      completedAt
    }
    guestFeedbackThemes
    managerHandoffNotes
    completedDays
    inputTokens
    outputTokens
    model
    promptVersion
    generatedAt
    isCurrent
    reused
  }
`;

export const GET_PROPERTY_MANAGER_SUMMARY = gql`
  ${PROPERTY_MANAGER_SUMMARY_FIELDS}
  query GetPropertyManagerSummary($propertyId: ID!) {
    propertyManagerSummary(propertyId: $propertyId) {
      ...PropertyManagerSummaryFields
    }
  }
`;

export const GENERATE_PROPERTY_MANAGER_SUMMARY = gql`
  ${PROPERTY_MANAGER_SUMMARY_FIELDS}
  mutation GeneratePropertyManagerSummary($propertyId: ID!, $force: Boolean!) {
    generatePropertyManagerSummary(propertyId: $propertyId, force: $force) {
      ...PropertyManagerSummaryFields
    }
  }
`;
